import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { SCREEN_TYPES } from 'utils/constants';
import { processTrackingId } from 'utils/tracking';
import { useAppStore, useReceiptStore, useTrackingStore } from 'store';
import { useProductStore } from 'store/productStore';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { getFedexId, scanId } from 'services/barcodes/checkFedexId';
import { BarcodeType, ReceiptData } from 'types';
import { handleCloseReceipt } from '../utils';
import { ProblemScreen } from './ProblemScreen';
import { cn } from 'utils/classnames';

export default function ScanShippingBox() {
  const [loading, setLoading] = useState(false);
  const [problemData, setProblemData] = useState<ReceiptData | null>(null);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(trackingInputRef);
  const { setCurrentScreen, setBarcodeDebugMessage } = useAppStore();
  const { receiptData: cachedReceiptData } = useReceiptStore();
  const { lastTrackingId, currentTrackingId, setCurrentTrackingId } = useTrackingStore();
  const { setProductNew, setProductCondition, setProductId, setCurrentProduct } = useProductStore();

  useEffect(() => {
    setCurrentTrackingId('');
    requestAnimationFrame(() => trackingInputRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const el = trackingInputRef.current;
      if (el && !el.disabled && document.activeElement !== el) el.focus();
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const handleCloseReceiptClick = (): void => {
    // Check if items were scanned (receipt_ind='y')
    console.log('ConfirmClose check - receiptData:', cachedReceiptData);
    console.log('Lines:', cachedReceiptData?.lines);
    const hasScannedItems = cachedReceiptData?.lines?.some((line) => {
      console.log('Line receipt_ind:', line?.receipt_ind);
      return line?.receipt_ind === 'y';
    });
    console.log('hasScannedItems:', hasScannedItems);
    if (hasScannedItems) {
      // Show ConfirmClose page only if items were scanned
      setCurrentScreen(SCREEN_TYPES.CONFIRM_CLOSE);
    } else {
      // Close directly if no items scanned
      handleCloseReceipt();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedTrackingId = currentTrackingId.trim();

    if (!trimmedTrackingId) {
      toast.warning('Please enter tracking number');
      return;
    }

    // Check for FedEx patterns
    const fedexId = getFedexId(trimmedTrackingId);
    if (fedexId !== trimmedTrackingId) {
      setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
      setCurrentTrackingId(fedexId);
      await scanId(trimmedTrackingId);
      requestAnimationFrame(() => trackingInputRef?.current?.focus());
      return;
    }

    // Check for other barcode patterns
    const barcodeType = isInputBarcode(trimmedTrackingId) as BarcodeType | null;
    if (barcodeType) {
      setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
      getBarcodeAction(barcodeType, SCREEN_TYPES.SCAN_BOX, {
        setProductNew,
        setProductCondition,
        setBarcodeDebugMessage,
        setProductId,
        setTrackingId: setCurrentTrackingId,
        setCurrentProduct,
      });
      setCurrentTrackingId('');
      return;
    }

    setProblemData(null);
    // Process as regular tracking ID

    await processTrackingId({
      trackingId: trimmedTrackingId,
      source: 'manual',
      onLoading: setLoading,
      trackingInputRef,
      onProblem: setProblemData,
    });

    // Clear input only on success (processTrackingId will handle loading state)
    // If successful, the screen will change to SCAN_ITEM
    // If failed, user stays on this screen with the input intact for correction
  };

  const scanInput = (inResult: boolean) => (
    <div className="flex items-center gap-2.5">
      <Input
        ref={trackingInputRef}
        id="tracking-id"
        type="text"
        inputMode={keyboardVisible ? 'text' : 'none'}
        placeholder="Input field"
        value={currentTrackingId}
        onChange={(e) => setCurrentTrackingId(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmit();
          }
        }}
        onBlur={() => {
          if (!loading) requestAnimationFrame(() => trackingInputRef.current?.focus());
        }}
        disabled={loading}
        autoFocus
        autoComplete="off"
        spellCheck={false}
        className={cn('text-base', inResult && 'bg-white/95 border-white/30')}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          'h-12 w-12 shrink-0 rounded-xl',
          inResult ? 'bg-white/90 border-white/30' : 'border-slate-300'
        )}
        onClick={toggleKeyboardInput}
        aria-pressed={keyboardVisible}
      >
        <Keyboard className="h-5 w-5" />
      </Button>
    </div>
  );

  if (problemData) {
    return (
      <ProblemScreen
        result={{
          responseMsg: problemData.responseMsg,
          tracking_id: problemData.tracking_id || problemData.receipt?.tracking_id,
        }}
        scanInput={scanInput(true)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Scan Shipping Box
      </CardTitle>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label htmlFor="tracking-id" className="mb-2 block text-sm font-semibold text-slate-700">
          Tracking #
        </Label>
        {scanInput(false)}
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="mr-2 text-sm font-semibold text-slate-600">Previous Tracking #</span>
        <span className="break-all text-sm font-semibold text-slate-900">
          {lastTrackingId || ''}
        </span>
      </div>

      <Button
        onClick={handleCloseReceiptClick}
        disabled={!lastTrackingId}
        className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold text-white shadow-sm hover:bg-emerald-700"
      >
        CLOSE RECEIPT
      </Button>
    </div>
  );
}
